import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import { BUDGET_TIMELINE_TYPE } from '../BudgetTimeline';

// Employee/Function data structure for hierarchical displays
interface FunctionData {
  displayName: string;
  subtitle?: string;
  designation?: string;
  profilePhoto?: string;
  isEmployee?: boolean;
  isGroup?: boolean;
  level: 'ORG' | 'COMPANY' | 'DIVISION' | 'BU' | 'DEPARTMENT' | 'EMPLOYEE';
  displayLevel?: string | null;
  isHighlighted?: boolean;
}

// Base stakeholder row structure
interface BaseStakeholderRow {
  id: number;
  NOTE_ID?: string | number;
  [columnId: string]: unknown;
}

// Flat stakeholder row (used in StakeHolderFlatListTable)
interface StakeholderRow extends BaseStakeholderRow {
  RAISED_FOR?: unknown;
  PROPOSER?: string;
  APPROVER?: string;
  PUBLISHER?: string;
  publisherSelectedEmployees?: SelectedEmployee[];
  proposerSelectedEmployees?: SelectedEmployee[];
  approverSelectedEmployees?: SelectedEmployee[];
}

export interface SelectedEmployee {
  id: number;
  value: number;
  label: string;
  profilePhoto: string;
}

// Tree stakeholder row (used in StakeHolderTreeTable)
export interface StakeholderTreeRow extends BaseStakeholderRow {
  function: FunctionData;
  hierarchy: string[];
  PROPOSER: string;
  APPROVER: string;
  PUBLISHER: string;
  publisherSelectedEmployees?: SelectedEmployee[];
  proposerSelectedEmployees?: SelectedEmployee[];
  approverSelectedEmployees?: SelectedEmployee[];
}

// Current pay details row (used in CurrentPayDetailsTreeTable)
interface CurrentPayDetailsRow {
  id: string | number;
  function: FunctionData;
  hierarchy: string[];
  currentPayroll: number | string;
  percentageIncrease: number | string;
  increasedBudget?: number;
  totalNewBudget: number;
}

// Employee data structure for approver allocation
export interface Employee {
  id: number;
  companyId?: number | null;
  displayName: string;
  profilePhoto: string;
  designation?: string | null;
  ownerType: 'OWNER' | 'COLLABORATOR';
  employeeFunction?: string | null;
  email: string;
  personalEmail?: string | null;
  workEmail?: string | null;
  preferredName?: string | null;
}

// Approver wise allocation row (used in ApproverWiseAllocationTable)
interface ApproverWiseAllocationRow extends BaseStakeholderRow {
  RAISED_FOR?: unknown;
  EMPLOYEES?: Employee[];
  employees?: Employee[];
  currentPayroll: number;
  increasedAllocation: number;
  totalAllocation: number;
  adjustedBudget?: number;
}

// Updated types for the slice
interface CompensationDrawersState {
  isOpen: boolean;
  originalStakeholdersRows: StakeholderRow[];
  modifiedStakeholdersRows: StakeholderRow[];
  originalStakeholdersTreeRows: StakeholderTreeRow[];
  modifiedStakeholdersTreeRows: StakeholderTreeRow[];
  originalCurrentPayDetailsRows: CurrentPayDetailsRow[];
  modifiedCurrentPayDetailsRows: CurrentPayDetailsRow[];
  originalApproverWiseAllocationRows: ApproverWiseAllocationRow[];
  modifiedApproverWiseAllocationRows: ApproverWiseAllocationRow[];
  budgetTimelineStatus: BUDGET_TIMELINE_TYPE;
  isBudgetDrawerOpen: boolean;
  originalCreateGuidelineRows: StakeholderRow[];
  modifiedCreateGuidelineRows: StakeholderRow[];
  isCreateGuidelineDrawerOpen: boolean;
  isFlaggedProposalsFilterApplied: boolean;
  isCompensationRecommendationBottomSheetOpen: boolean;
}

export interface CompensationRootState {
  compensationDrawers: CompensationDrawersState;
}

const initialState: CompensationDrawersState = {
  isOpen: false,
  originalStakeholdersRows: [],
  modifiedStakeholdersRows: [],
  originalStakeholdersTreeRows: [],
  modifiedStakeholdersTreeRows: [],
  originalCurrentPayDetailsRows: [],
  modifiedCurrentPayDetailsRows: [],
  originalApproverWiseAllocationRows: [],
  modifiedApproverWiseAllocationRows: [],
  budgetTimelineStatus: null,
  isBudgetDrawerOpen: false,
  originalCreateGuidelineRows: [],
  modifiedCreateGuidelineRows: [],
  isCreateGuidelineDrawerOpen: false,
  isFlaggedProposalsFilterApplied: false,
  isCompensationRecommendationBottomSheetOpen: false,
};

export const compensationDrawersSlice = createSlice({
  name: 'compensationDrawers',
  initialState,
  reducers: {
    openWorkflowStakeholdersDrawer: (state) => {
      state.isOpen = true;
    },
    closeWorkflowStakeholdersDrawer: (state) => {
      state.isOpen = false;
    },
    toggleWorkflowStakeholdersDrawer: (state) => {
      state.isOpen = !state.isOpen;
    },
    setOriginalStakeholdersRows: (state, action) => {
      state.originalStakeholdersRows = action.payload;
    },
    setModifiedStakeholdersRows: (state, action) => {
      state.modifiedStakeholdersRows = action.payload;
    },
    setOriginalStakeholdersTreeRows: (state, action) => {
      state.originalStakeholdersTreeRows = action.payload;
    },
    setModifiedStakeholdersTreeRows: (state, action) => {
      state.modifiedStakeholdersTreeRows = action.payload;
    },
    setOriginalCurrentPayDetailsRows: (state, action) => {
      state.originalCurrentPayDetailsRows = action.payload;
    },
    setModifiedCurrentPayDetailsRows: (state, action) => {
      state.modifiedCurrentPayDetailsRows = action.payload;
    },
    setOriginalApproverWiseAllocationRows: (state, action) => {
      state.originalApproverWiseAllocationRows = action.payload;
    },
    setModifiedApproverWiseAllocationRows: (state, action) => {
      state.modifiedApproverWiseAllocationRows = action.payload;
    },
    setBudgetTimelineStatus: (state, action) => {
      state.budgetTimelineStatus = action.payload;
    },
    setIsBudgetDrawerOpen: (state, action) => {
      state.isBudgetDrawerOpen = action.payload;
    },
    setIsCreateGuidelineDrawerOpen: (state, action) => {
      state.isCreateGuidelineDrawerOpen = action.payload;
    },
    setOriginalCreateGuidelineRows: (state, action) => {
      state.originalCreateGuidelineRows = action.payload;
    },
    setModifiedCreateGuidelineRows: (state, action) => {
      state.modifiedCreateGuidelineRows = action.payload;
    },
    setIsFlaggedProposalsFilterApplied: (state, action) => {
      state.isFlaggedProposalsFilterApplied = action.payload;
    },

    setIsCompensationRecommendationBottomSheetOpen: (state, action) => {
      state.isCompensationRecommendationBottomSheetOpen = action.payload;
    },
  },
});

export const {
  openWorkflowStakeholdersDrawer,
  closeWorkflowStakeholdersDrawer,
  toggleWorkflowStakeholdersDrawer,
  setOriginalStakeholdersRows,
  setModifiedStakeholdersRows,
  setOriginalStakeholdersTreeRows,
  setModifiedStakeholdersTreeRows,
  setOriginalCurrentPayDetailsRows,
  setModifiedCurrentPayDetailsRows,
  setOriginalApproverWiseAllocationRows,
  setModifiedApproverWiseAllocationRows,
  setBudgetTimelineStatus,
  setIsBudgetDrawerOpen,
  setIsCreateGuidelineDrawerOpen,
  setOriginalCreateGuidelineRows,
  setModifiedCreateGuidelineRows,
  setIsFlaggedProposalsFilterApplied,
  setIsCompensationRecommendationBottomSheetOpen,
} = compensationDrawersSlice.actions;

export const useCompensationDrawersState = () =>
  useSelector((state: CompensationRootState) => state.compensationDrawers);

export default compensationDrawersSlice.reducer;
